import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCheck, faTasks, faLightbulb, faPlus, faTrash, faPencilAlt, 
  faChevronDown, faChevronUp, faEye, faEyeSlash 
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';

/**
 * Enhanced Todo Component with suggestion integration and plot item references
 * Now with vertical action buttons option for drawer layout
 */
const TodoList = ({
  todoItems,
  onTodoItemToggle,
  onTodoItemDelete,
  onTodoItemEdit,
  onAddTodoItem,
  onToggleTodoItemEdit,
  suggestions,
  setSuggestions,
  workspaceUuid,
  workspaceData,
  plotItems,
  vertical = false // New prop to control action buttons layout
}) => {
  const todoContainerRef = useRef(null);

const [isLoadingImplementations, setIsLoadingImplementations] = useState(false);
  const [isSuggestionsModalOpen, setIsSuggestionsModalOpen] = useState(false);
  const [currentTodoItem, setCurrentTodoItem] = useState(null);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [isSuggestionDetailsModalOpen, setIsSuggestionDetailsModalOpen] = useState(false);
  const [expandedSuggestionSections, setExpandedSuggestionSections] = useState({});

  // Toggle suggestion section for a specific todo item
  const toggleSuggestionSection = (todoId) => {
    setExpandedSuggestionSections(prev => ({
      ...prev,
      [todoId]: !prev[todoId]
    }));
  };

  // Expand all suggestion sections
  const expandAllSuggestions = () => {
    const expanded = {};
    todoItems.forEach(item => {
      if (getLinkedSuggestionCount(item.id) > 0) {
        expanded[item.id] = true;
      }
    });
    setExpandedSuggestionSections(expanded);
  };

  // Hide all suggestion sections
  const hideAllSuggestions = () => {
    setExpandedSuggestionSections({});
  };

  // Get all suggestions linked to a todo item
  const getLinkedSuggestions = (todoId) => {
    const linkedSuggestions = [];
    
    // Check each domain in suggestions
    Object.keys(suggestions || {}).forEach(domain => {
      // Check each target in the domain
      Object.keys(suggestions[domain] || {}).forEach(targetId => {
        // Check each suggestion
        (suggestions[domain][targetId] || []).forEach(suggestion => {
          // First check if this suggestion has linkedTodoIds
          if (suggestion.linkedTodoIds && suggestion.linkedTodoIds.includes(todoId)) {
            linkedSuggestions.push({
              ...suggestion,
              domain,
              targetId
            });
          }
        });
      });
    });
    
    return linkedSuggestions;
  };

  // Count linked suggestions for a todo item
  const getLinkedSuggestionCount = (todoId) => {
    return getLinkedSuggestions(todoId).length;
  };

  // Count total linked suggestions across all todo items
  const getTotalSuggestionCount = () => {
    let count = 0;
    if (Array.isArray(todoItems)) {
      todoItems.forEach(item => {
        if (item && item.id) {
          count += getLinkedSuggestionCount(item.id);
        }
      });
    }
    return count;
  };

  const scrollToBottom = () => {
    if (todoContainerRef.current) {
      const container = todoContainerRef.current;
      container.scrollTop = container.scrollHeight;
    }
  };
  
  // "Add New Item" button handler
  const handleAddNewItem = () => {
    // Call the parent component's add function
    onAddTodoItem();
    
    // Use setTimeout to ensure the DOM has updated before scrolling
    setTimeout(() => {
      scrollToBottom();
    }, 100);
  };
  
  const handleGetImplementations = async (plotItem, suggestion, idx) => {
      try {
        setIsLoadingImplementations(true);
        
        const response = await axios.post(`/workspace/${workspaceUuid}/plot-item/${plotItem.id}/get-implementations`, {
          suggestion,
          workspaceData
        });
        
        // The implementations are complete plot items
        const { implementations } = response.data;
        
        // Get the current suggestions
        const updatedSuggestions = [...(suggestions?.plot_items?.[plotItem.id] || [])];
        
        // Update the suggestion to include the implementations
        updatedSuggestions[idx] = {
          ...suggestion,
          implementations, // These are complete plot items
          isExpanded: true
        };
        
        // Update the suggestions in the main state
        setSuggestions((prev) => {
          const updated = { ...prev };
          
          if (!updated.plot_items) {
            updated.plot_items = {};
          }
          
          updated.plot_items[plotItem.id] = updatedSuggestions;
          return updated;
        });
        
        // Update the local modal state
        if (isSuggestionsModalOpen && currentPlotItem && currentPlotItem.id === plotItem.id) {
          setCurrentSuggestions(updatedSuggestions);
        }
      } catch (error) {
        console.error('Failed to get implementations:', error);
      } finally {
        setIsLoadingImplementations(false);
      }
    };
  
  // Get todo suggestions (for getting new suggestions for a todo item)
  const handleGetTodoSuggestions = async (todoId, e) => {
    if (e) {
      e.stopPropagation(); // Prevent other click handlers
    }
    
    try {
      setIsLoadingSuggestions(true);
      
      const response = await axios.post(`/workspace/${workspaceUuid}/todo/${todoId}/get-suggestions`, {
        workspaceData
      });
      
      if (response.data.success && response.data.suggestions) {
        // Update suggestions and link them to this todo item
        setSuggestions(prev => {
          const updated = { ...prev };
          
          // Process each domain (plot_items, characters, theme)
          Object.keys(response.data.suggestions).forEach(domain => {
            if (!updated[domain]) {
              updated[domain] = {};
            }
            
            // Process each target within the domain
            Object.keys(response.data.suggestions[domain]).forEach(targetId => {
              if (!updated[domain][targetId]) {
                updated[domain][targetId] = [];
              }
              
              // Add linkedTodoIds to each new suggestion
              const newSuggestions = response.data.suggestions[domain][targetId].map(
                suggestion => ({
                  ...suggestion,
                  linkedTodoIds: [todoId]
                })
              );
              
              // Merge the new suggestions
              updated[domain][targetId] = [
                ...updated[domain][targetId],
                ...newSuggestions
              ];
            });
          });
          
          // Automatically expand suggestion section for this todo item
          setExpandedSuggestionSections(prev => ({
            ...prev,
            [todoId]: true
          }));
          
          return updated;
        });
      }
    } catch (error) {
      console.error('Failed to get todo suggestions:', error);
    } finally {
      setIsLoadingSuggestions(false);
    }
  };

  // Get suggestions for all todos
  const handleGetAllTodoSuggestions = async () => {
    try {
      setIsLoadingSuggestions(true);
      
      // Make a request for each incomplete todo item
      const incompleteTodos = todoItems.filter(item => !item.completed);
      
      for (const todo of incompleteTodos) {
        await handleGetTodoSuggestions(todo.id);
      }
    } catch (error) {
      console.error('Failed to get all todo suggestions:', error);
    } finally {
      setIsLoadingSuggestions(false);
    }
  };

  // Open the suggestions modal for a todo item
  const handleOpenSuggestionsModal = (todoItem, e) => {
    if (e) {
      e.stopPropagation(); // Prevent toggle suggestion section
    }
    setCurrentTodoItem(todoItem);
    setIsSuggestionsModalOpen(true);
  };

  // Open suggestion details modal for a specific suggestion
  const handleOpenSuggestionDetailsModal = (suggestion, todoItem) => {
    setSelectedSuggestion(suggestion);
    setCurrentTodoItem(todoItem);
    setIsSuggestionDetailsModalOpen(true);
  };

  // Handle modal backdrop click
  const handleModalBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      setIsSuggestionsModalOpen(false);
      setIsSuggestionDetailsModalOpen(false);
    }
  };

  // Remove the link between a todo item and a suggestion
  const handleRemoveTodoSuggestionLink = (todoId, suggestion, domain, targetId) => {
    setSuggestions(prev => {
      const updated = { ...prev };
      
      if (updated[domain] && updated[domain][targetId]) {
        // Find the suggestion and remove the todoId from its linkedTodoIds
        updated[domain][targetId] = updated[domain][targetId].map(s => {
          if (s === suggestion) {
            return {
              ...s,
              linkedTodoIds: (s.linkedTodoIds || []).filter(id => id !== todoId)
            };
          }
          return s;
        });
      }
      
      return updated;
    });
  };

  // Implement a suggestion directly from the suggestion details modal
  const handleImplementSuggestion = (suggestion) => {
    // Implementation depends on the domain and suggestion type
    // For plot_items domain:
    if (suggestion.domain === 'plot_items' && plotItems) {
      const plotItem = plotItems.find(item => item.id === suggestion.targetId);
      if (plotItem) {
        // Add implementation logic here
        console.log(`Implement suggestion for plot item #${plotItem.number || 'N/A'}`);
        
        // Here you would call the parent's implementation function
        // For example: onImplementPlotItemSuggestion(plotItem.id, suggestion);
        
        // Close the modal after implementation
        setIsSuggestionDetailsModalOpen(false);
      }
    }
  };

  // Get plot item reference for a suggestion
  const getPlotItemReference = (suggestion) => {
    if (suggestion.domain === 'plot_items' && plotItems) {
      const plotItem = plotItems.find(item => item.id === suggestion.targetId);
      if (plotItem) {
        return {
          number: plotItem.number,
          title: plotItem.title || `Beat ${plotItem.number}`,
          content: plotItem.content || ''
        };
      }
    }
    return null;
  };

  // Count the total number of todos with linked suggestions
  const getTodosWithSuggestionsCount = () => {
    return todoItems.filter(item => getLinkedSuggestionCount(item.id) > 0).length;
  };

  // Render action buttons horizontally or vertically based on the prop
// Updated renderActionButtons function for compact vertical buttons

const renderActionButtons = (item) => {
  if (vertical) {
    // Vertical compact layout - just icons stacked, with tooltips on hover
    return (
      <div className="d-flex flex-column align-items-center todo-vertical-buttons">
        {/* Complete checkbox */}
        <div title="Mark as complete">
          <input
            className="form-check-input"
            type="checkbox"
            checked={item.completed}
            onChange={() => onTodoItemToggle(item.id)}
            id={`checkbox-${item.id}`}
          />
        </div>
        
        {/* Get Suggestions button */}
        {!item.completed && !item.isEditing && (
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={(e) => handleGetTodoSuggestions(item.id, e)}
            disabled={isLoadingSuggestions}
            title="Get suggestions for this todo"
          >
            <FontAwesomeIcon icon={faLightbulb} />
          </button>
        )}
        
        {/* Edit button */}
        {item.isEditing ? (
          <button
            className="btn btn-sm btn-success"
            onClick={() => onTodoItemEdit(item.id, item.content, true)}
            title="Save changes"
          >
            <FontAwesomeIcon icon={faCheck} />
          </button>
        ) : (
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={(e) => {
              e.stopPropagation();
              onToggleTodoItemEdit(item.id);
            }}
            title="Edit this todo"
          >
            <FontAwesomeIcon icon={faPencilAlt} />
          </button>
        )}
        
        {/* Delete button */}
        <button
          className="btn btn-sm btn-outline-danger"
          onClick={(e) => {
            e.stopPropagation();
            onTodoItemDelete(item.id);
          }}
          title="Delete this todo"
        >
          <FontAwesomeIcon icon={faTrash} />
        </button>
      </div>
    );
  } else {
    // Horizontal layout (original)
    return (
      <>
        <div className="d-flex align-items-center flex-grow-1">
          <div className="form-check me-3">
            <input
              className="form-check-input"
              type="checkbox"
              checked={item.completed}
              onChange={() => onTodoItemToggle(item.id)}
              id={`checkbox-${item.id}`}
            />
          </div>
          {/* Content will go here in the parent component */}
        </div>
        <div className="btn-group ms-2">
          {/* Get Suggestions button */}
          {!item.completed && !item.isEditing && (
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={(e) => handleGetTodoSuggestions(item.id, e)}
              disabled={isLoadingSuggestions}
            >
              <FontAwesomeIcon icon={faLightbulb} />
              {isLoadingSuggestions ? ' ...' : ''}
            </button>
          )}
          
          {/* Edit/Save button */}
          {item.isEditing ? (
            <button
              className="btn btn-sm btn-success"
              onClick={() => onTodoItemEdit(item.id, item.content, true)}
            >
              Save
            </button>
          ) : (
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => onToggleTodoItemEdit(item.id)}
            >
              <FontAwesomeIcon icon={faPencilAlt} />
            </button>
          )}
          
          {/* Delete button */}
          <button
            className="btn btn-sm btn-outline-danger"
            onClick={() => onTodoItemDelete(item.id)}
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
        </div>
      </>
    );
  }
};

  return (
    <div className="todo-container" ref={todoContainerRef}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <button
            className="btn btn-sm btn-outline-primary me-2"
            onClick={handleGetAllTodoSuggestions}
            disabled={isLoadingSuggestions}
          >
            <FontAwesomeIcon icon={faLightbulb} className="me-1" />
            {isLoadingSuggestions ? 'Loading...' : 'Get Suggestions for All'}
          </button>
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={handleAddNewItem}
          >
            <FontAwesomeIcon icon={faPlus} className="me-1" />
            Add To Do
          </button>
        </div>
      </div>
     
      <div className="alert alert-info">
        These are the tasks you selected to improve this scene. Check them off as you complete them.
      </div>

      {/* Expand/Hide All buttons - only show if there are suggestions */}
      {getTotalSuggestionCount() > 0 && (
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <span className="text-muted me-2">
              {getTotalSuggestionCount()} suggestions across {getTodosWithSuggestionsCount()} tasks
            </span>
          </div>
          <div className="btn-group">
            <button 
              className="btn btn-sm btn-outline-secondary" 
              onClick={expandAllSuggestions}
              title="Expand all suggestions"
            >
              <FontAwesomeIcon icon={faEye} className="me-1" />
              Show All
            </button>
            <button 
              className="btn btn-sm btn-outline-secondary" 
              onClick={hideAllSuggestions}
              title="Hide all suggestions"
            >
              <FontAwesomeIcon icon={faEyeSlash} className="me-1" />
              Hide All
            </button>
          </div>
        </div>
      )}
     
      {todoItems.length > 0 ? (
        <div className="list-group">
          {todoItems.map((item) => {
            const linkedSuggestions = getLinkedSuggestions(item.id);
            const suggestionsCount = linkedSuggestions.length;
            const isExpanded = !!expandedSuggestionSections[item.id];
            
            return (
              <div key={item.id} className="mb-3">
                <div
                  className="list-group-item d-flex justify-content-between align-items-start"
                  style={item.completed ? { backgroundColor: '#f8f9fa' } : {}}
                >
                  {vertical ? (
                    // Vertical layout
                    <div className="d-flex" style={{ width: '100%' }}>
                      <div className="flex-grow-1">
                        {item.isEditing ? (
                          <textarea
                            className="form-control mb-2"
                            value={item.content}
                            onChange={(e) => onTodoItemEdit(item.id, e.target.value)}
                            rows={3}
                            autoFocus
                          />
                        ) : (
                          <div>
                            <span
                              style={item.completed ? {
                                textDecoration: 'line-through',
                                color: '#6c757d'
                              } : {}}
                            >
                              {item.content}
                            </span>
                            
                            {/* Source badge if available */}
                            {item.source && (
                              <span className={`badge ${
                                item.source === 'evaluation' ? 'bg-info' : 'bg-secondary'
                              } ms-2`}>
                                {item.source === 'evaluation' ? 'Evaluation' : 'Suggestion'}
                              </span>
                            )}
                            
                            {/* Linked suggestions badge with toggle button */}
                            {suggestionsCount > 0 && (
                              <div className="d-inline-block ms-2">
                                <button
                                  className="btn btn-sm btn-warning text-dark"
                                  onClick={(e) => handleOpenSuggestionsModal(item, e)}
                                  style={{ marginRight: '4px' }}
                                >
                                  <FontAwesomeIcon icon={faLightbulb} className="me-1" />
                                  {suggestionsCount}
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={() => toggleSuggestionSection(item.id)}
                                  aria-expanded={isExpanded}
                                  aria-controls={`suggestion-section-${item.id}`}
                                  title={isExpanded ? "Hide suggestions" : "Show suggestions"}
                                >
                                  <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {renderActionButtons(item)}
                    </div>
                  ) : (
                    // Horizontal layout (original)
                    <>
                      <div className="d-flex align-items-center flex-grow-1">
                        <div className="form-check me-3">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={item.completed}
                            onChange={() => onTodoItemToggle(item.id)}
                            id={`checkbox-${item.id}`}
                          />
                        </div>
                       
                        {item.isEditing ? (
                          <textarea
                            className="form-control"
                            value={item.content}
                            onChange={(e) => onTodoItemEdit(item.id, e.target.value)}
                            rows={3}
                            autoFocus
                          />
                        ) : (
                          <div className="flex-grow-1">
                            <span
                              style={item.completed ? {
                                textDecoration: 'line-through',
                                color: '#6c757d'
                              } : {}}
                            >
                              {item.content}
                            </span>
                            
                            {/* Source badge if available */}
                            {item.source && (
                              <span className={`badge ${
                                item.source === 'evaluation' ? 'bg-info' : 'bg-secondary'
                              } ms-2`}>
                                {item.source === 'evaluation' ? 'Evaluation' : 'Suggestion'}
                              </span>
                            )}
                            
                            {/* Linked suggestions badge with toggle button */}
                            {suggestionsCount > 0 && (
                              <div className="d-inline-block ms-2">
                                <button
                                  className="btn btn-sm btn-warning text-dark"
                                  onClick={(e) => handleOpenSuggestionsModal(item, e)}
                                  style={{ marginRight: '4px' }}
                                >
                                  <FontAwesomeIcon icon={faLightbulb} className="me-1" />
                                  {suggestionsCount}
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={() => toggleSuggestionSection(item.id)}
                                  aria-expanded={isExpanded}
                                  aria-controls={`suggestion-section-${item.id}`}
                                  title={isExpanded ? "Hide suggestions" : "Show suggestions"}
                                >
                                  <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                     
                      <div className="btn-group ms-2">
                        {/* Get Suggestions button */}
                        {!item.completed && !item.isEditing && (
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={(e) => handleGetTodoSuggestions(item.id, e)}
                            disabled={isLoadingSuggestions}
                          >
                            <FontAwesomeIcon icon={faLightbulb} />
                            {isLoadingSuggestions ? ' ...' : ''}
                          </button>
                        )}
                        
                        {item.isEditing ? (
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => onTodoItemEdit(item.id, item.content, true)}
                          >
                            Save
                          </button>
                        ) : (
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => onToggleTodoItemEdit(item.id)}
                          >
                            <FontAwesomeIcon icon={faPencilAlt} />
                          </button>
                        )}
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => onTodoItemDelete(item.id)}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </>
                  )}
                </div>

                {/* Expanded linked suggestions - now toggleable */}
                {suggestionsCount > 0 && isExpanded && (
                  <div 
                    id={`suggestion-section-${item.id}`}
                    className="linked-suggestions ps-4 pt-2 pb-2 border-start border-bottom border-end rounded-bottom"
                    style={{ marginTop: '-1px', backgroundColor: '#f8f9fa' }}
                  >
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <small className="text-muted">Linked Suggestions ({suggestionsCount}):</small>
                      <button 
                        className="btn btn-sm btn-outline-secondary" 
                        onClick={() => toggleSuggestionSection(item.id)}
                      >
                        <FontAwesomeIcon icon={faChevronUp} />
                      </button>
                    </div>
                    {linkedSuggestions.map((suggestion, index) => {
                      // Get plot item info from either our helper function or directly from the suggestion
                      const plotItemRef = suggestion.plotItemReference || getPlotItemReference(suggestion);
                      const beatNumber = plotItemRef?.number || 
                                        (suggestion.targetId?.includes('beat_') ? 
                                         suggestion.targetId.replace('beat_', '') : 'N/A');
                      
                      return (
                        <div 
                          key={index} 
                          className="suggestion-card p-2 mb-2 bg-white border rounded"
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleOpenSuggestionDetailsModal(suggestion, item)}
                        >
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <div>
                              {/* Domain badge */}
                              <span className={`badge ${
                                suggestion.domain === 'plot_items' ? 'bg-primary' : 
                                suggestion.domain === 'characters' ? 'bg-success' : 
                                'bg-info'
                              } me-2`}>
                                {suggestion.domain === 'plot_items' ? 'Plot Item' : 
                                suggestion.domain === 'characters' ? 'Character' : 
                                'Theme'}
                              </span>
                              
                              {/* Beat number badge - now with more reliable extraction */}
                              <span className="badge bg-secondary me-2">
                                Beat #{beatNumber}
                              </span>
                              
                              {/* Action badge if available */}
                              {suggestion.action && (
                                <span className={`badge ${
                                  suggestion.action === 'replace' ? 'bg-danger' : 
                                  suggestion.action === 'edit' ? 'bg-primary' : 
                                  'bg-success'
                                } me-2`}>
                                  {suggestion.action === 'replace' ? 'Replace' : 
                                  suggestion.action === 'edit' ? 'Edit' : 
                                  'Append'}
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {/* Truncated content preview */}
                          <p className="mb-0 text-truncate">
                            {suggestion.content.length > 60 
                              ? `${suggestion.content.substring(0, 60)}...` 
                              : suggestion.content}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-muted">No to-do items. You can add custom items or select suggestions from the workspace scene page.</p>
      )}

      {/* Linked Suggestions Modal */}
      {isSuggestionsModalOpen && currentTodoItem && (
        <div
          className="modal show"
          style={{ display: 'block', backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
          onClick={handleModalBackdropClick}
        >
          <div className="modal-dialog modal-lg" style={{ marginTop: '10vh', maxHeight: '80vh' }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Suggestions Linked to Todo Item
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setIsSuggestionsModalOpen(false)}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                {/* Todo Item Details */}
                <div className="alert alert-secondary mb-3">
                  <div className="d-flex align-items-center">
                    <div className="form-check me-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={currentTodoItem.completed}
                        onChange={() => onTodoItemToggle(currentTodoItem.id)}
                        disabled
                      />
                    </div>
                    <strong>{currentTodoItem.content}</strong>
                  </div>
                </div>
                
                {/* Linked Suggestions */}
                <h6>Linked Suggestions</h6>
                {getLinkedSuggestions(currentTodoItem.id).length > 0 ? (
                  <div className="list-group">
                    {getLinkedSuggestions(currentTodoItem.id).map((suggestion, idx) => {
                      const plotItemRef = getPlotItemReference(suggestion);
                      
                      return (
                        <div key={idx} className="list-group-item mb-2">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div>
                              {/* Domain badge */}
                              <span className={`badge ${
                                suggestion.domain === 'plot_items' ? 'bg-primary' : 
                                suggestion.domain === 'characters' ? 'bg-success' : 
                                'bg-info'
                              } me-2`}>
                                {suggestion.domain === 'plot_items' ? 'Plot Item' : 
                                suggestion.domain === 'characters' ? 'Character' : 
                                'Theme'}
                              </span>
                              
                              {/* Plot Item reference (new) */}
                              {plotItemRef && (
                                <span className="badge bg-secondary me-2">
                                  Beat #{plotItemRef.number || 'N/A'}: {plotItemRef.title}
                                </span>
                              )}
                              
                              {/* Action badge if available */}
                              {suggestion.action && (
                                <span className={`badge ${
                                  suggestion.action === 'replace' ? 'bg-danger' : 
                                  suggestion.action === 'edit' ? 'bg-primary' : 
                                  'bg-success'
                                } me-2`}>
                                  {suggestion.action === 'replace' ? 'Replace' : 
                                  suggestion.action === 'edit' ? 'Edit' : 
                                  'Append'}
                                </span>
                              )}
                              
                              {/* Source badge if available */}
                              {suggestion.source === 'evaluation' && (
                                <span className="badge bg-info me-2">Evaluation</span>
                              )}
                            </div>
                            <div>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleRemoveTodoSuggestionLink(
                                  currentTodoItem.id,
                                  suggestion,
                                  suggestion.domain,
                                  suggestion.targetId
                                )}
                              >
                                Unlink
                              </button>
                            </div>
                          </div>
                          
                          {/* Suggestion details */}
                          <div className="p-2 bg-light rounded mb-2">
                            <p className="mb-1">{suggestion.content}</p>
                          </div>
                          
                          {suggestion.theory && (
                            <div className="small text-muted mb-1">
                              <strong>Theory:</strong> {suggestion.theory}
                            </div>
                          )}
                          {suggestion.impact && (
                            <div className="small text-muted mb-1">
                              <strong>Impact:</strong> {suggestion.impact}
                            </div>
                          )}
                          
                          {/* Action buttons for suggestions */}
                          {suggestion.domain === 'plot_items' && (
                            <div className="d-flex justify-content-end mt-2">
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => handleOpenSuggestionDetailsModal(suggestion, currentTodoItem)}
                              >
                                View Details
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-center text-muted">No suggestions linked to this todo item.</p>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setIsSuggestionsModalOpen(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Suggestion Details Modal - Similar to PlotItems suggestion modal */}
      {isSuggestionDetailsModalOpen && selectedSuggestion && (
        <div
          className="modal show"
          style={{ display: 'block', backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
          onClick={handleModalBackdropClick}
        >
          <div className="modal-dialog modal-lg" style={{ marginTop: '10vh', maxHeight: '80vh' }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Suggestion Details
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setIsSuggestionDetailsModalOpen(false)}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                {/* Plot Item Reference (if applicable) */}
                {selectedSuggestion.domain === 'plot_items' && (
                  <div className="alert alert-secondary mb-3">
                    {(() => {
                      const plotItemRef = getPlotItemReference(selectedSuggestion);
                      if (plotItemRef) {
                        return (
                          <>
                            <h6>Referenced Beat #{plotItemRef.number}: {plotItemRef.title}</h6>
                            <div className="p-2 bg-white rounded">
                              <p className="mb-0">{plotItemRef.content.length > 200 
                                ? `${plotItemRef.content.substring(0, 200)}...` 
                                : plotItemRef.content}
                              </p>
                            </div>
                          </>
                        );
                      }
                      return <p>No plot item reference available</p>;
                    })()}
                  </div>
                )}
                
                {/* Suggestion Content */}
                <div className="mb-3">
                  <h6>Suggestion {selectedSuggestion.action && (
                    <span className="badge bg-primary ms-2">
                      {selectedSuggestion.action}
                    </span>
                  )}</h6>
                  <div className="p-3 bg-light rounded">
                    <p className="mb-0">{selectedSuggestion.content}</p>
                  </div>
                </div>
                
                {/* Suggestion Details */}
                {selectedSuggestion.theory && (
                  <div className="mb-3">
                    <h6>Theory</h6>
                    <p className="mb-0">{selectedSuggestion.theory}</p>
                  </div>
                )}
                
                {selectedSuggestion.impact && (
                  <div className="mb-3">
                    <h6>Impact</h6>
                    <p className="mb-0">{selectedSuggestion.impact}</p>
                  </div>
                )}
                
                {/* Linked Todo Items */}
                <div className="mb-3">
                  <h6>Linked Todo Items</h6>
                  <ul className="list-group">
                    <li className="list-group-item">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={currentTodoItem.completed}
                          readOnly
                          disabled
                        />
                        <label className="form-check-label">
                          {currentTodoItem.content}
                        </label>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="modal-footer">
                {/* Action buttons for implementing the suggestion */}
                {selectedSuggestion.domain === 'plot_items' && (
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={() => handleGetImplementations(currentPlotItem, suggestion, idx)}
                    >
                    {isLoadingImplementations ? 'Loading...' : 'Get Options'}
                    </button>
                )}
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setIsSuggestionDetailsModalOpen(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

TodoList.propTypes = {
  todoItems: PropTypes.array.isRequired,
  onTodoItemToggle: PropTypes.func.isRequired,
  onTodoItemDelete: PropTypes.func.isRequired,
  onTodoItemEdit: PropTypes.func.isRequired,
  onAddTodoItem: PropTypes.func.isRequired,
  onToggleTodoItemEdit: PropTypes.func.isRequired,
  suggestions: PropTypes.object,
  setSuggestions: PropTypes.func.isRequired,
  workspaceUuid: PropTypes.string.isRequired,
  workspaceData: PropTypes.object.isRequired,
  plotItems: PropTypes.array, // For plot item references
  vertical: PropTypes.bool // Controls the action buttons layout
};

export default TodoList;